// src/anim/animController.ts — Dövüşçü başına mixer (ARCHITECTURE §5.2/§5.6, ANIMATION §4/§5/§8). AnimDecision'ı three'ye uygular:
//   • üç action ailesi: base (tam gövde ya da #lower), upper (#upper; base o an #lower), additive (Hit_A+, ağırlık additiveWeight);
//     aynı kemiğe iki katman asla yazmaz (track kümesi ayrımı — PropertyMixer normalize eder, override yok);
//   • crossfade: süre = max(next.fade, prev.fadeOut); `crossFadeTo` gelen action'ı play() ETMEZ → her geçişte enabled/reset/time/paused/
//     timeScale/play elle; warp kullanılmaz (tS her karede setEffectiveTimeScale ile biz sürüyoruz);
//   • no-op karşılaştırması (clip, startTick): LoopOnce + clampWhenFinished biten action'a paused=true yazar; aynı klip yeni startTick ile
//     gelince reset().play() — aksi hâlde art arda iki L1 / iki Dodge ikinci seferde donuk kalır;
//   • faz aktarımı: loko klipleri arasında next.time = prev.time / prev.dur · next.dur; aynı kanonik klibin tam/#lower sürümü arasında
//     time aynen (katman girişinde bacaklar zıplamaz);
//   • syncTime: |action.time − syncTime| > SYNC_EPS ise sıçrat (hitstop çıkışı, kare kaybı); pauseAt: kanonik sn'de dondur;
//   • hitstop: d.paused → mixer.timeScale = 0 (crossfade de donar); sunum snap: hamlenin ilk 3 karesi tS +%20;
//   • update: mixer.update + kök hareket bastırma (root + hips XZ dinlenmeye) + pinHipsY ise hips Y dinlenmeye (Y-bastır rejimi);
//   • jointWorld: VFX için eklem dünya konumu; silah ucu = handslot + quat·(0, L, 0) (setWeaponLength ile L);
//   • soketler: resolveSockets — handslotl/handslotr; Mannequin_Medium'da el kemiğine ölçülmüş dinlenme dönüşümüyle SANAL soket
//     (adı sanitize edilmiş → klip handslot kanalları PropertyBinding ile buna da bağlanır; viewer/actor.js aynen).
// Sıcak yolda tahsis yok (modül scratch vektörleri; action'lar mixer önbelleğinden).
import * as THREE from 'three';
import type { AnimController, AnimDecision, AnimPlay, Joint, Vec3 } from '../core/types.ts';
import { T } from '../config/tuning.ts';
import { CLIPS, canonicalOf } from '../config/clipTable.ts';
import { MOVES } from '../config/moves.ts';
import { isPoseClip } from './bodyMask.ts';
import type { ClipLibrary } from './clipLibrary.ts';

/* ------------------------------------------------------------- yerel sabitler */
/** Sim–görsel klip zamanı sapma eşiği (ARCHITECTURE §5.2 "> 0.05 s"; tuning'de yok → needsWP0). */
export const SYNC_EPS = 0.05;
/** Rig_Medium dinlenme: handslot yerel dönüşümü (ANIMATION §7 ölçüm; sanal soket için) ve hips yüksekliği. */
export const SOCKET_T: readonly [number, number, number] = [0, 0.0961, -0.0575];
export const SOCKET_Q_L: readonly [number, number, number, number] = [0, 0, -0.7071, 0.7071];
export const SOCKET_Q_R: readonly [number, number, number, number] = [0, 0, 0.7071, 0.7071];
export const REST_HIPS_Y = 0.406;
/** Faz aktarımı yapılan loko klipleri (kanonik). */
const LOKO_CANON: ReadonlySet<string> = new Set([CLIPS.WALKING_A, CLIPS.RUNNING_A, CLIPS.RUNNING_B, CLIPS.WALKING_BACK, CLIPS.STRAFE_L, CLIPS.STRAFE_R, CLIPS.CRAWLING]);
/** Sunum snap yalnız HAMLE klipleri (DESIGN §11 "hamlenin ilk 3 karesi"): MoveDef + MoveStage klipleri. Hit_A/B, BlockEnter, Jump_Land,
 *  GetUp, Spawn, Knockdown gibi durum klipleri (syncTime verseler de) hızlanmaz — hitstun'a tS ile sığdırılmış klip erken biterdi.
 *  needsWP0: AnimDecision.snap bayrağı ile animMap'in kendisi işaretlesin (sentetik/test hamleleri MOVES dışında kalıyor). */
const MOVE_CLIPS: ReadonlySet<string> = new Set(MOVES.flatMap((m) => [m.clip, ...(m.stages?.map((s) => s.clip) ?? [])]));

/** three, kemik adlarındaki `.` gibi karakterleri siler (PropertyBinding.sanitizeNodeName): 'handslot.l' → 'handslotl'. */
export const normName = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const JOINT_BONE: Readonly<Record<Joint, string>> = {
  'hand.l': 'handl', 'hand.r': 'handr', 'foot.l': 'footl', 'foot.r': 'footr', 'toes.l': 'toesl', 'toes.r': 'toesr', head: 'head',
  'weapon.tip': 'handslotr', 'weapon.mid': 'handslotr', 'weapon.tip.l': 'handslotl', 'weapon.mid.l': 'handslotl', center: 'hips',
};

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();

/* ------------------------------------------------------------- kemikler ve soketler */

/** Model ağacından kemik haritası (normalize ad → Object3D); root/hips ve handslotl/r adlı düğümler de alınır (Mannequin_Medium'da
 *  fighterView'ın controller kurulmadan ÖNCE el kemiğine eklediği SANAL soket Bone değildir; toplanmazsa jointWorld('weapon.tip') köke düşerdi). */
export function collectBones(root: THREE.Object3D, into: Map<string, THREE.Object3D> = new Map()): Map<string, THREE.Object3D> {
  root.traverse((o) => { if ((o as THREE.Bone).isBone || /^(root|hips|handslot[lr])$/i.test(o.name)) into.set(normName(o.name), o); });
  return into;
}

export interface Sockets { r: THREE.Object3D | null; l: THREE.Object3D | null; hasVirtual: boolean; holderR: THREE.Group; holderL: THREE.Group }
/** handslot.l/.r soketleri + `holder` grupları (prop ofseti: yalnız staff +0.75 yerel Y). Mannequin_Medium: el kemiğine sanal soket. */
export function resolveSockets(bones: Map<string, THREE.Object3D>): Sockets {
  let hasVirtual = false;
  const one = (side: 'l' | 'r'): THREE.Object3D | null => {
    const slot = bones.get('handslot' + side);
    if (slot) return slot;
    const hand = bones.get('hand' + side);
    if (!hand) return null;
    const virt = new THREE.Object3D();
    virt.name = 'handslot' + side;
    virt.position.set(SOCKET_T[0], SOCKET_T[1], SOCKET_T[2]);
    const q = side === 'l' ? SOCKET_Q_L : SOCKET_Q_R;
    virt.quaternion.set(q[0], q[1], q[2], q[3]);
    hand.add(virt);
    bones.set(virt.name, virt);
    hasVirtual = true;
    return virt;
  };
  const r = one('r'), l = one('l');
  const holderR = new THREE.Group(), holderL = new THREE.Group();
  holderR.name = 'holder'; holderL.name = 'holder';
  r?.add(holderR); l?.add(holderL);
  return { r, l, hasVirtual, holderR, holderL };
}

/* ------------------------------------------------------------- katman durumu */

interface Layer {
  action: THREE.AnimationAction | null;
  clip: string;        // istenen ad (AnimPlay.clip)
  resolved: string;    // kütüphanede çözülen ad (tembel yedek gelince değişir)
  startTick: number;
  fadeOut: number;
  loop: boolean;
  frames: number;      // bu oynatmada uygulanan kare sayısı (sunum snap)
}
const layer = (): Layer => ({ action: null, clip: '', resolved: '', startTick: 0, fadeOut: 0, loop: false, frames: 0 });

export interface AnimControllerOptions {
  /** Sunum snap kapalı (?anim sahnesi karşılaştırması / testler). */
  noSnap?: boolean;
}

export class AnimControllerImpl implements AnimController {
  readonly mixer: THREE.AnimationMixer;
  readonly bones: Map<string, THREE.Object3D>;
  readonly rootBone: THREE.Object3D | null;
  readonly hipsBone: THREE.Object3D | null;
  private readonly restRoot = new THREE.Vector3();
  private readonly restHips = new THREE.Vector3(0, REST_HIPS_Y, 0);
  private readonly base = layer();
  private readonly upper = layer();
  private additive: THREE.AnimationAction | null = null;
  private additiveWeight = 0;
  private pinHipsY = false;
  private weaponLenR = 0; private weaponLenL = 0;
  /** Son uygulanan karar alanları (HUD / debug). */
  paused = false;

  constructor(readonly root: THREE.Object3D, readonly lib: ClipLibrary, private readonly opts: AnimControllerOptions = {}) {
    this.mixer = new THREE.AnimationMixer(root);
    this.bones = collectBones(root);
    this.rootBone = this.bones.get('root') ?? null;
    this.hipsBone = this.bones.get('hips') ?? null;
    if (this.rootBone) this.restRoot.copy(this.rootBone.position);
    if (this.hipsBone) this.restHips.copy(this.hipsBone.position);
  }

  /** Silah ucu uzunlukları (Item.length; ITEMS.length katalog + holder ofseti). */
  setWeaponLength(right: number, left: number): void { this.weaponLenR = right; this.weaponLenL = left; }

  /** Şu anki base/upper action'ları (HUD, testler). */
  get baseAction(): THREE.AnimationAction | null { return this.base.action; }
  get upperAction(): THREE.AnimationAction | null { return this.upper.action; }
  get baseClip(): string { return this.base.resolved; }
  get upperClip(): string { return this.upper.resolved; }

  apply(d: AnimDecision): void {
    this.paused = d.paused;
    this.mixer.timeScale = d.paused ? 0 : 1;
    this.pinHipsY = d.pinHipsY;
    // syncTime: üst katman açıkken hamle senkronu üst action'a (base o an loop #lower), aksi hâlde base'e
    const upperOpen = d.upper.clip !== '';
    this.applyLayer(this.base, d.base, upperOpen ? -1 : d.syncTime, d.paused);
    this.applyLayer(this.upper, d.upper, upperOpen ? d.syncTime : -1, d.paused);
    this.applyAdditive(d.additiveWeight);
  }

  private applyLayer(L: Layer, play: AnimPlay, syncTime: number, paused: boolean): void {
    if (play.clip === '') {
      if (L.action) {
        const fade = L.fadeOut > 0 ? L.fadeOut : T.ANIM.fadeUpperIn;
        L.action.fadeOut(fade);               // ağırlık 0'a inince three action'ı devre dışı bırakır
        L.action = null;
      }
      L.clip = ''; L.resolved = ''; L.startTick = 0;
      return;
    }
    const resolved = this.lib.resolve(play.clip);
    const changed = L.clip !== play.clip || L.startTick !== play.startTick || L.resolved !== resolved;
    let action = L.action;
    if (changed || !action) {
      const clip = this.lib.get(resolved);
      const next = this.mixer.clipAction(clip);
      const pose = isPoseClip(clip);
      const prev = L.action;
      next.setLoop(play.loop && !pose ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
      next.clampWhenFinished = true;
      next.enabled = true;
      next.reset();
      // başlangıç zamanı: faz aktarımı (loko↔loko / aynı kanonik klibin tam↔#lower sürümü), aksi hâlde startAt (kanonik sn)
      let t = clamp(play.startAt, 0, Math.max(0, clip.duration - 1e-4));
      if (prev && prev !== next && play.loop && L.loop) {
        const pc = canonicalOf(L.resolved), nc = canonicalOf(resolved);
        const pd = prev.getClip().duration;
        if (pc === nc) t = clamp(prev.time, 0, clip.duration - 1e-4);
        else if (LOKO_CANON.has(pc) && LOKO_CANON.has(nc) && pd > 0) t = clamp(prev.time / pd * clip.duration, 0, clip.duration - 1e-4);
      }
      next.time = t;
      next.paused = false;
      next.setEffectiveTimeScale(play.rate);
      next.setEffectiveWeight(1);
      next.play();
      if (prev && prev !== next) {
        const fade = Math.max(play.fade, L.fadeOut);
        if (fade > 0) prev.crossFadeTo(next, fade, false);
        else prev.stop();
      }
      L.action = next; L.clip = play.clip; L.resolved = resolved; L.startTick = play.startTick;
      L.fadeOut = play.fadeOut; L.loop = play.loop; L.frames = 0;
      action = next;
    } else {
      // aynı oynatma: tS her karede (loko v/doğal; sunum snap ilk 3 karede +%20 — yalnız hamle klipleri, MOVE_CLIPS)
      let rate = play.rate;
      if (!this.opts.noSnap && syncTime >= 0 && L.frames < T.FEEL.snapFrames && MOVE_CLIPS.has(play.clip)) rate *= 1 + T.FEEL.snapRateBonus;
      action.setEffectiveTimeScale(rate);
      if (!paused) L.frames++;
      // senkron: sapma > SYNC_EPS ise sıçrat (pauseAt'a ulaşılmadıysa paused kalkar)
      if (syncTime >= 0 && !paused && Math.abs(action.time - syncTime) > SYNC_EPS) {
        action.time = syncTime;
        if (!(play.pauseAt >= 0 && syncTime >= play.pauseAt - 1e-6)) action.paused = false;
      }
    }
    // pauseAt / endAt (kanonik sn): donuk kare
    if (play.pauseAt >= 0 && action.time >= play.pauseAt - 1e-6) { action.time = play.pauseAt; action.paused = true; }
    else if (!play.loop && play.endAt >= 0 && action.time >= play.endAt - 1e-6) { action.time = play.endAt; action.paused = true; }
  }

  private applyAdditive(weight: number): void {
    if (weight <= 0) {
      if (this.additive && this.additiveWeight > 0) { this.additive.setEffectiveWeight(0); this.additive.stop(); }
      this.additiveWeight = 0;
      return;
    }
    if (!this.lib.has(CLIPS.HIT_A_ADD)) return;
    if (!this.additive) {
      this.additive = this.mixer.clipAction(this.lib.get(CLIPS.HIT_A_ADD));
      this.additive.setLoop(THREE.LoopOnce, 1);
      this.additive.clampWhenFinished = true;
    }
    const a = this.additive;
    if (this.additiveWeight <= 0 || weight > this.additiveWeight + 1e-6) {   // yeni flinch (ağırlık yükseldi): baştan
      a.enabled = true; a.reset(); a.time = 0; a.paused = false; a.setEffectiveTimeScale(1); a.play();
    }
    a.setEffectiveWeight(weight);
    this.additiveWeight = weight;
  }

  update(dtRender: number): void {
    this.mixer.update(dtRender);
    // kök hareket bastırma: root + hips XZ dinlenmeye (ölçüm: dodge'larda root da hareket ediyor); Y rejimi pinHipsY
    if (this.rootBone) { this.rootBone.position.x = this.restRoot.x; this.rootBone.position.z = this.restRoot.z; }
    if (this.hipsBone) {
      this.hipsBone.position.x = this.restHips.x; this.hipsBone.position.z = this.restHips.z;
      if (this.pinHipsY) this.hipsBone.position.y = this.restHips.y;
    }
  }

  /** Eklem dünya konumu (VFX; bir kare gecikmeli matrixWorld kabul). Silah ucu/ortası: handslot + quat·(0, L, 0). */
  jointWorld(out: Vec3, joint: Joint): Vec3 {
    const bone = this.bones.get(JOINT_BONE[joint]);
    if (!bone) { this.root.getWorldPosition(_v); out.x = _v.x; out.y = _v.y; out.z = _v.z; return out; }
    bone.getWorldPosition(_v);
    if (joint === 'weapon.tip' || joint === 'weapon.mid' || joint === 'weapon.tip.l' || joint === 'weapon.mid.l') {
      const left = joint.endsWith('.l');
      const len = (left ? this.weaponLenL : this.weaponLenR) * (joint.startsWith('weapon.mid') ? 0.5 : 1);
      bone.getWorldQuaternion(_q);
      // yerel +Y ekseni dünyaya: q · (0,1,0)
      const x = 2 * (_q.x * _q.y - _q.w * _q.z), y = 1 - 2 * (_q.x * _q.x + _q.z * _q.z), z = 2 * (_q.y * _q.z + _q.w * _q.x);
      _v.x += x * len; _v.y += y * len; _v.z += z * len;
    }
    out.x = _v.x; out.y = _v.y; out.z = _v.z;
    return out;
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.root);
    this.base.action = null; this.upper.action = null; this.additive = null;
  }
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Fabrika (WP9: `createController: (root, f, def) => createAnimController(root, lib)`). */
export function createAnimController(root: THREE.Object3D, lib: ClipLibrary, opts?: AnimControllerOptions): AnimControllerImpl {
  return new AnimControllerImpl(root, lib, opts);
}
